import { FC } from "react";
import { useCancelOrgBillingTransferRequestMutation } from "@api/orgBillingTransferRequest.api";
import { BillingTransferRequestStatus } from "@common/constants/domains/billing/billingTransferRequest";
import { validateClientBillingTransferPlan } from "@common/helpers/permissions/organizationRequestValidators";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { getVisibleOrgBillingTransferRequest } from "../helpers/getVisibleOrgBillingTransferRequest";
import { ClientDetailsRow } from "../views/clientViewConfig";
import { useCancelTransferRequest } from "./useCancelTransferRequest";

interface Props {
    client: ClientDetailsRow;
    onTransfer: (client: ClientDetailsRow) => void;
}

export const TransferBillingToClientMenuItems: FC<Props> = ({
    client,
    onTransfer,
}) => {
    const activeOrganization = useActiveOrganization();
    const cancelOrgBillingTransferMutation =
        useCancelOrgBillingTransferRequestMutation();
    const cancelTransferRequest = useCancelTransferRequest();

    const orgBillingTransferRequest = getVisibleOrgBillingTransferRequest({
        organization: activeOrganization,
        client,
    });
    const isOrgBillingTransferPending =
        orgBillingTransferRequest?.status ===
        BillingTransferRequestStatus.PENDING;
    const planNotTransferableError = validateClientBillingTransferPlan(
        client.plan,
    );

    return (
        <>
            <Menu.Item
                onClick={() => onTransfer(client)}
                disabled={
                    isOrgBillingTransferPending ||
                    !client.hasUsersToTransferBillingTo ||
                    !!planNotTransferableError
                }
                disabledReason={
                    isOrgBillingTransferPending
                        ? "A billing transfer request is already pending"
                        : (planNotTransferableError ??
                          "Invite at least one admin to the workspace to transfer billing ownership.")
                }
            >
                Transfer billing to client
            </Menu.Item>
            {isOrgBillingTransferPending && activeOrganization && (
                <Menu.Item
                    onClick={() =>
                        cancelTransferRequest({
                            cancel: () =>
                                cancelOrgBillingTransferMutation.mutateAsync({
                                    requestId: orgBillingTransferRequest.id,
                                    organizationId: activeOrganization.id,
                                }),
                            successMessage: "Canceled billing transfer request",
                            errorMessage:
                                "Failed to cancel billing transfer request",
                        })
                    }
                    disabled={cancelOrgBillingTransferMutation.isPending}
                >
                    Cancel billing transfer
                </Menu.Item>
            )}
        </>
    );
};
